import React, { useEffect, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";
import { FaFilter, FaSort, FaTh, FaList, FaSearch } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { HiOutlineDocumentDuplicate } from "react-icons/hi2";
import SortDropdown from "./SortDropdown";
import { ArrowDownUp, ListFilter, Download } from "lucide-react";
import TableSkeleton from "./TableSkelaton";

const ReusableTable = ({
  columns,
  data,
  onSearch,
  onDelete,
  onDuplicate,
  onDownload,
  onDownloadCSV,
  gridView,
  isLoading = false,
  onSort,
  pageCount,
  manualPagination = false,
  onPaginationChange,
  totalCount,
  initialPageIndex = 0,
  initialPageSize = 10,
  getRowId,
  onSelectAllAcrossPagesChange,
}) => {
  const [searchValue, setSearchValue] = useState("");
  // "" means no explicit column filter (default search)
  const [selectedColumn, setSelectedColumn] = useState("");
  const [selectAllAcrossPages, setSelectAllAcrossPages] = useState(false);
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState({});
  const [isGridView, setIsGridView] = useState(gridView);
  const [sortModalOpen, setSortModalOpen] = useState(false);

  const capitalizeFirstLetter = (string) => {
    return string
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const capitalizedColumns = columns.map(column => ({
    ...column,
    header: typeof column.header === 'string' 
      ? capitalizeFirstLetter(column.header)
      : column.header
  }));

  const [pagination, setPagination] = useState({
    pageIndex: initialPageIndex || 0,
    pageSize: initialPageSize || 10,
  });

  useEffect(() => {
    setPagination({
      pageIndex: initialPageIndex || 0,
      pageSize: initialPageSize || 10,
    });
  }, [initialPageIndex, initialPageSize]);

  const handlePaginationChange = (updater) => {
    const newPagination = typeof updater === 'function' 
      ? updater(pagination)
      : updater;
    setPagination(newPagination);
    if (manualPagination && onPaginationChange) {
      onPaginationChange(newPagination);
    }
  };

  const table = useReactTable({
    data,
    columns: capitalizedColumns,
    state: {
      rowSelection: selectedRows,
      pagination: pagination,
    },
    onRowSelectionChange: (updater) => {
      const updatedSelection = updater instanceof Function ? updater(selectedRows) : updater;
      setSelectedRows(updatedSelection);
    },
    onPaginationChange: handlePaginationChange,
    getRowId: getRowId || ((row) => row.id || row.name || String(row)),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: manualPagination,
    pageCount: pageCount,
  });

  const selectedRowCount = Object.keys(table.getState().rowSelection).length;

  const handleGlobalSelectAll = () => {
    setSelectAllAcrossPages(true);
    if (onSelectAllAcrossPagesChange) {
      onSelectAllAcrossPagesChange(true);
    }
  };

  const handleClearGlobalSelectAll = () => {
    setSelectAllAcrossPages(false);
    setSelectedRows({});
    table.resetRowSelection?.();
    if (onSelectAllAcrossPagesChange) {
      onSelectAllAcrossPagesChange(false);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    // Changing search should clear any existing selection and global select-all
    if (selectedRowCount > 0 || selectAllAcrossPages) {
      handleClearGlobalSelectAll();
    }
    setSearchValue(value);
    if (onSearch) {
      onSearch(value, selectedColumn);
    }
  };

  const handleSort = (sortOption) => {
    onSort(sortOption);
    setSortModalOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sortModalOpen && !event.target.closest('.sort-menu') && 
          !event.target.closest('.btn-light')) {
        setSortModalOpen(false);
      }

      // Close filter dropdown when clicking outside
      if (filterDropdownOpen && !event.target.closest('.filter-menu') && !event.target.closest('.filter-button')) {
        setFilterDropdownOpen(false);
      }
    };
  
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [sortModalOpen, filterDropdownOpen]);

  return (
    <div className="table-container">
      <div className="table-toolbar d-flex justify-content-between align-items-center mb-3">
        <div className="toolbar-left d-flex align-items-center gap-2">
          <div className="search-input-container" style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder={
                selectedColumn === ""
                  ? "Document Name"
                  : selectedColumn === "entity"
                  ? "Entity Name"
                  : selectedColumn === "agent"
                  ? "Agent Name"
                  : selectedColumn === "source"
                  ? "Source"
                  : selectedColumn === "status"
                  ? "Status"
                  : selectedColumn === "run_status"
                  ? "Run Status"
                  : selectedColumn === "exception_status"
                  ? "Exception Status"
                  : "Search"
              }
              value={searchValue}
              onChange={handleSearch}
              className="search-input"
              style={{ backgroundColor: '#f0f0f0', paddingRight: '30px' }}
            />
            <FaSearch style={{ 
              position: 'absolute',
              right: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#666'
            }} />
          </div>
         
        </div>
        <div className="toolbar-right d-flex gap-2">
          {selectedRowCount > 0 && (onDownload || onDownloadCSV) && (
            <button
              className="btn"
              onClick={() => {
                if (onDownloadCSV) {
                  const selectedRowData = table.getSelectedRowModel().rows.map(row => row.original);
                  onDownloadCSV(selectedRowData);
                } else if (onDownload) {
                  const selectedRowData = table.getSelectedRowModel().rows.map(row => row.original);
                  onDownload(selectedRowData);
                }
              }}
              style={{
                backgroundColor: '#fce7f3',
                color: '#000',
                border: 'none',
                borderRadius: '8px',
                padding: '6px 20px',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                height: '36px'
              }}
            >
              <Download size={16} />
              Download
            </button>
          )}
         
          <div className="position-relative">
            <button
              className={`btn d-flex align-items-center gap-1 filter-button ${
                selectedColumn ? "btn-secondary" : "btn-light"
              }`}
              type="button"
              onClick={() => setFilterDropdownOpen((v) => !v)}
              style={
                selectedColumn
                  ? { borderRadius: "8px" }
                  : { borderRadius: "8px" }
              }
            >
              <ListFilter size={16} /> Filter
            </button>

            {filterDropdownOpen && (
              <div
                className="filter-menu"
                style={{
                  position: "absolute",
                  right: 0,
                  top: "calc(100% + 8px)",
                  zIndex: 1000,
                  background: "white",
                  border: "1px solid #e5e7eb",
                  borderRadius: "10px",
                  boxShadow: "0 10px 25px rgba(0,0,0,0.08)",
                  minWidth: "220px",
                  padding: "8px",
                }}
              >
                {[
                  { id: "", label: "Document" },
                  { id: "entity", label: "Entity" },
                  { id: "agent", label: "Agent" },
                  { id: "source", label: "Source" },
                  { id: "status", label: "Status" },
                  { id: "run_status", label: "Run Status" },
                  { id: "exception_status", label: "Exception Status" },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    className="btn w-100 text-start"
                    onClick={() => {
                      // Changing filter should also clear selection and global select-all
                      if (selectedRowCount > 0 || selectAllAcrossPages) {
                        handleClearGlobalSelectAll();
                      }
                      setSelectedColumn(opt.id);
                      if (onSearch) {
                        onSearch(searchValue, opt.id);
                      }
                      setFilterDropdownOpen(false);
                    }}
                    style={{
                      background: selectedColumn === opt.id ? "#f3f4f6" : "transparent",
                      border: "none",
                      borderRadius: "8px",
                      padding: "8px 10px",
                      fontSize: "14px",
                      color: "#111827",
                    }}
                  >
                    {opt.label}
                  </button>
                ))}

                {selectedColumn !== "" && (
                  <button
                    type="button"
                    className="btn w-100 text-start"
                    onClick={() => {
                      if (selectedRowCount > 0 || selectAllAcrossPages) {
                        handleClearGlobalSelectAll();
                      }
                      setSelectedColumn("");
                      if (onSearch) {
                        onSearch(searchValue, "");
                      }
                      setFilterDropdownOpen(false);
                    }}
                    style={{
                      marginTop: "6px",
                      background: "transparent",
                      border: "none",
                      borderRadius: "8px",
                      padding: "8px 10px",
                      fontSize: "14px",
                      color: "#6b7280",
                    }}
                  >
                    Clear filter
                  </button>
                )}
              </div>
            )}
          </div>
         {/* <div className="sort-button-container">
            <button
              className="btn btn-light"
              onClick={() => setSortModalOpen(!sortModalOpen)}
            >
              <ArrowDownUp /> Sort
            </button>
            <SortDropdown 
              isOpen={sortModalOpen}
              onSort={handleSort}
              onClose={() => setSortModalOpen(false)}
            />
          </div> */}
          
        {/* <div className="btn-group" role="group" aria-label="View toggle">
            <button
              type="button"
              className={`btn ${!isGridView ? 'btn-secondary' : 'btn-light'}`}
              onClick={() => setIsGridView(false)}
            >
              <FaList />
            </button>
            <button
              type="button"
              className={`btn ${isGridView ? 'btn-secondary' : 'btn-light'}`}
              onClick={() => setIsGridView(true)}
            >
              <FaTh />
            </button>
          </div> */}
        </div>
      </div>
      {(table.getIsAllPageRowsSelected?.() || selectAllAcrossPages) && (
        <div
          className="mt-1 px-3 py-2 d-flex justify-content-center"
          style={{
            backgroundColor: "#f3f4f6",
            borderRadius: "8px",
            fontSize: "14px",
            textAlign: "center",
          }}
        >
          {!selectAllAcrossPages ? (
            <>
              <span>All rows on this page are selected. </span>
              <button
                type="button"
                className="btn btn-link p-0 align-baseline text-primary"
                style={{ fontSize: "14px" }}
                onClick={handleGlobalSelectAll}
              >
                Select all {totalCount} rows
              </button>
            </>
          ) : (
            <>
              <span>All {totalCount} rows are selected. </span>
              <button
                type="button"
                className="btn btn-link p-0 align-baseline text-primary"
                style={{ fontSize: "14px" }}
                onClick={handleClearGlobalSelectAll}
              >
                Clear selection
              </button>
            </>
          )}
        </div>
      )}

    
      <hr />

      {isLoading ? (
        <TableSkeleton isGridView={isGridView} columns={table.getAllColumns().length} rows={5} />
      ) : !isGridView ? (
        <div className="table-wrapper">
          <table className="table custom-table">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th key={header.id}>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid-view">
          {table.getRowModel().rows.map((row) => (
            <div key={row.id} className="grid-item">
              <div className="grid-item-header">
                <h5 className="mb-0">{row.original.name || 'Item'}</h5>
                <input
                  type="checkbox"
                  className="grid-item-checkbox form-check-input"
                  checked={!!selectedRows[row.id]}
                  onChange={() =>
                    setSelectedRows((prev) => ({
                      ...prev,
                      [row.id]: !prev[row.id],
                    }))
                  }
                />
              </div>
              <div className="grid-item-content">
                {row.getVisibleCells().map((cell) => (
                  <div key={cell.id} className="grid-item-field">
                    <span className="grid-item-label">
                      {typeof cell.column.columnDef.header === 'string'
                        ? capitalizeFirstLetter(cell.column.columnDef.header)
                        : cell.column.columnDef.header}
                    </span>
                    <span className="grid-item-value">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="pagination d-flex">
        <div className="rows-per-page">
          <span>Show rows:</span>
          <select
            className="rows-dropdown"
            value={table.getState().pagination.pageSize}
            onChange={(e) => {
              const newSize = Number(e.target.value);
              table.setPageSize(newSize);
              if (manualPagination && onPaginationChange) {
                onPaginationChange({
                  pageIndex: 0,
                  pageSize: newSize,
                });
              }
            }}
          >
            {[5, 10, 15, 25].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </select>
        </div>

        <div className="pagination-info">
          {table.getState().pagination.pageIndex *
            table.getState().pagination.pageSize +
            1}
          -
          {manualPagination && totalCount
            ? Math.min(
                (table.getState().pagination.pageIndex + 1) *
                  table.getState().pagination.pageSize,
                totalCount
              )
            : Math.min(
                (table.getState().pagination.pageIndex + 1) *
                  table.getState().pagination.pageSize,
                data.length
              )}{" "}
          of {manualPagination && totalCount ? totalCount : data.length}
        </div>

        <div className="pagination-buttons">
          <button
            className="btn"
            onClick={() => {
              table.previousPage();
              if (manualPagination && onPaginationChange) {
                onPaginationChange({
                  pageIndex: table.getState().pagination.pageIndex - 1,
                  pageSize: table.getState().pagination.pageSize,
                });
              }
            }}
            disabled={!table.getCanPreviousPage()}
          >
            {"<"}
          </button>
          <button
            className="btn"
            onClick={() => {
              table.nextPage();
              if (manualPagination && onPaginationChange) {
                onPaginationChange({
                  pageIndex: table.getState().pagination.pageIndex + 1,
                  pageSize: table.getState().pagination.pageSize,
                });
              }
            }}
            disabled={!table.getCanNextPage()}
          >
            {">"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReusableTable;